<template>
    <page :tabs="tabs" :busy="busy">
        <b-alert v-if="!isImpersonated && userIsUnlayerTester" show variant="info" class="mt-2 w-50 ml-2">
            Old templates are now locked following the deployment of our updated email/document editor
            or because your account is currently transitioning. For all template creation and modification,
            please utilize the new template editor system.
        </b-alert>
        <div v-if="isImpersonated && userIsUnlayerTester && rowData.length > 0" class="ml-2 mt-2 mb-3 w-50">
            <div class="d-flex justify-content-between align-items-center mb-1">
                <span>Migration Progress: {{ migratedResponses }} of {{ totalResponses }} templates migrated</span>
            </div>
            <div class="d-flex justify-content-between align-items-center mb-1">
                <span
                    >Default Templates: {{ defaultTemplatesCount }} | Custom Templates: {{ customTemplatesCount }}</span
                >
            </div>
            <b-progress height="2rem">
                <b-progress-bar :value="migrationPercentage">
                    <strong>{{ migrationPercentage }}%</strong>
                </b-progress-bar>
            </b-progress>
        </div>

        <grid
            :columnDefs="columnDefs"
            :isAutoWidth="true"
            ref="grid"
            :rowData="rowData"
            :defaultColDef="defaultColDef"
            :autoGroupColumnDef="autoGroupColumnDef"
            :treeData="true"
            :getDataPath="getDataPath"
            :isGroupOpenByDefault="() => expanded"
        >
            <responsive-button
                :icon="['fas', 'right-left']"
                class="mr-2"
                v-if="isImpersonated"
                variant="outline-primary"
                @click="$bvModal.show('migration-modal')"
            >
                Migrate Default Responses Only
            </responsive-button>
            <responsive-button
                :icon="['fas', 'right-left']"
                class="mr-2"
                v-if="isImpersonated"
                variant="outline-primary"
                @click="$bvModal.show('migration-custom-templates-modal')"
            >
                Migrate Custom Responses Only
            </responsive-button>
            <responsive-button
                :disabled="userIsUnlayerTester"
                :icon="['fas', 'plus']"
                variant="primary"
                @click="createResponse"
                >Add Response</responsive-button
            >
            <responsive-button
                :icon="['fas', 'up-right-and-down-left-from-center']"
                variant="primary"
                @click="onButtonExpand"
            >
                <span v-if="expanded">Collapse</span>
                <span v-else>Expand</span>
            </responsive-button>
        </grid>
        <modal
            id="migration-modal"
            title="Migrate Default Templates"
            @ok="migrateDefaultTemplates"
        >
            <b-alert show variant="info">
                Only modified default templates will be migrated. Unmodified templates do not need to be migrated as they are already available in the new editor.
            </b-alert>
            Are you sure you want to migrate all default templates?
        </modal>
        <modal
            id="migration-custom-templates-modal"
            title="Migrate Custom Templates"
            @ok="migrateCustomTemplates"
        >
            Are you sure you want to migrate all custom templates?
        </modal>
    </page>
</template>

<script>
import Page from '../generic/page/page.vue';
import Grid from '../generic/grid/grid.vue';
import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
import ActionsCellRenderer from './actions-cell-renderer.vue';
import { formatResponseError } from '../../utils/api';
import api from '../../api';
import { RESPONSES_DATA_ENDPOINT } from '../../endpoints';
import { escapeHTML } from '../../utils/strings';
import { mapGetters } from 'vuex';
import Modal from '../generic/modal/modal.vue';

export default {
    components: {
        Page,
        Grid,
        ResponsiveButton,
        actionsCellRenderer: ActionsCellRenderer,
        Modal,
    },
    data() {
        return {
            busy: false,
            rowData: [],
            defaultColDef: {
                minWidth: 80,
                sortable: false,
            },
            autoGroupColumnDef: {
                flex: 2,
                minWidth: 300,
                resizable: true,
                sortable: true,
                comparator: (valueA, valueB, nodeA, nodeB) => {
                    const nameA = nodeA.data.title || nodeA.data.area_name;
                    const nameB = nodeB.data.title || nodeB.data.area_name;
                    return nameA > nameB ? 1 : -1;
                },
                headerName: 'Component / Title',
                cellStyle: { textAlign: 'center' },
                cellRendererParams: {
                    innerRenderer: ({ data }) => escapeHTML(data.title || data.area_name),
                },
            },
            getDataPath: (data) => data.id_hierarchy,
            gridApi: null,
            expanded: true,
        };
    },
    mounted() {
        const grid = this.$refs.grid;
        this.gridApi = grid.gridOptions.api;
        this.fetchResponses().then(() => {
            this.expandLevel0();
        });
    },
    computed: {
        ...mapGetters('header', ['userIsUnlayerTester', 'isImpersonated']),
        tabs() {
            return [
                {
                    title: 'Responses',
                    route: '/app/responses/list/',
                    active: true,
                },
            ];
        },
        migratedResponses() {
            return this.rowData.filter((item) => item.response_id && item.is_migrated).length;
        },
        totalResponses() {
            return this.rowData.filter((item) => item.response_id).length;
        },
        defaultTemplatesCount() {
            return this.rowData.filter((item) => item.response_id && item.is_default && !item.is_migrated).length;
        },
        customTemplatesCount() {
            return this.rowData.filter((item) => item.response_id && !item.is_default && !item.is_migrated).length;
        },
        migrationPercentage() {
            if (this.totalResponses === 0) return 0;
            return Math.round((this.migratedResponses / this.totalResponses) * 100);
        },
        columnDefs() {
            return [
                {
                    field: 'area_name',
                    headerName: 'Component',
                    hide: true,
                },
                {
                    field: 'title',
                    headerName: 'Title',
                    hide: true,
                },
                ...(this.isImpersonated
                    ? [
                          {
                              field: 'is_migrated',
                              headerName: 'Migrated to Editor',
                              type: 'yesNoColumnNew',
                              minWidth: 180,
                              cellClass: 'text-center',
                              headerClass: 'ag-centered-header',
                              cellRendererParams: ({ data }) => ({
                                  invisible: !data.response_id,
                              }),
                          },
                          {
                              field: 'is_changed',
                              headerName: 'Default Template Changed',
                              cellClass: 'text-center',
                              headerClass: 'ag-centered-header',
                              minWidth: 180,
                              cellRenderer: ({ data }) => {
                                  if (data.response_id) {
                                      if (data.is_changed) {
                                          return `<span class="badge badge-warning">Changed</span>`;
                                      } else if (!data.is_default) {
                                          return `<span class="badge badge-info">Custom</span>`;
                                      }
                                  }
                                  return '';
                              },
                          },
                      ]
                    : []),
                {
                    field: 'is_default',
                    headerName: 'Type',
                    minWidth: 100,
                    cellRenderer: ({ data }) =>
                        data && data.response_id ? (data.is_default ? 'Default' : 'User') : '',
                },
                {
                    field: '',
                    headerName: '',
                    sortable: false,
                    cellRenderer: 'actionsCellRenderer',
                    minWidth: 180,
                    maxWidth: 180,
                    cellRendererParams: ({ node }) => {
                        return {
                            onReset: async (id, hideModal, responseName) => {
                                try {
                                    this.busy = true;
                                    await api.post(`${RESPONSES_DATA_ENDPOINT(id)}reset/`);
                                    hideModal();
                                    this.successToast({
                                        text: `Response "${responseName}" successfully reset.`,
                                    });
                                    this.fetchResponses();
                                } catch (e) {
                                    const { generic } = formatResponseError(e);
                                    this.errorToast({
                                        text: generic || 'Could not reset response',
                                    });
                                } finally {
                                    this.busy = false;
                                }
                            },
                            onRemove: async (id, hideModal, responseName) => {
                                try {
                                    this.busy = true;
                                    await api.delete(`${RESPONSES_DATA_ENDPOINT(id)}`);
                                    if (node.parent.allChildrenCount === 1) {
                                        this.gridApi.applyTransaction({
                                            remove: [node, node.parent],
                                        });
                                    } else {
                                        this.gridApi.applyTransaction({
                                            remove: [node],
                                        });
                                    }
                                    hideModal();
                                    this.successToast({
                                        text: `Response "${responseName}" successfully deleted.`,
                                    });
                                } catch (e) {
                                    const { generic } = formatResponseError(e);
                                    this.errorToast({
                                        text: generic || 'Could not remove response',
                                    });
                                } finally {
                                    this.busy = false;
                                }
                            },
                            onMigrate: async (id, hideModal, responseName) => {
                                try {
                                    this.busy = true;
                                    await api.put(`${RESPONSES_DATA_ENDPOINT(id)}migrate/`);
                                    hideModal();
                                    this.successToast({
                                        text: `Response "${responseName}" successfully migrated.`,
                                    });
                                    this.fetchResponses();
                                } catch (e) {
                                    const { generic } = formatResponseError(e);
                                    this.errorToast({
                                        text: generic || 'Could not migrate response',
                                    });
                                } finally {
                                    this.busy = false;
                                }
                            },
                        };
                    },
                },
            ];
        },
    },
    methods: {
        async fetchResponses() {
            try {
                this.busy = true;
                const { data } = await api.get(RESPONSES_DATA_ENDPOINT());
                this.rowData = data;
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({
                    text: generic || 'Could not fetch responses',
                });
            } finally {
                this.busy = false;
            }
        },
        createResponse() {
            this.$router.push(`/app/responses/edit/0`);
        },
        onButtonExpand() {
            if (this.expanded) {
                this.gridApi.collapseAll();
                this.expanded = false;
            } else {
                this.expandLevel0();
            }
        },
        expandLevel0() {
            this.gridApi.forEachNode((node) => {
                if (node.level === 0) {
                    node.setExpanded(true);
                }
            });
            this.expanded = true;
        },
        async migrateDefaultTemplates() {
            try {
                this.busy = true;
                await api.put(`${RESPONSES_DATA_ENDPOINT()}migrate-default-responses/`);
                this.successToast({
                    text: `All templates successfully migrated.`,
                });
                this.fetchResponses();
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({
                    text: generic || 'Could not migrate all templates',
                });
            } finally {
                this.busy = false;
            }
        },
        async migrateCustomTemplates() {
            try {
                this.busy = true;
                await api.post(`${RESPONSES_DATA_ENDPOINT()}migrate-custom-responses/`);
                this.successToast({
                    text: `All templates successfully migrated.`,
                });
                this.fetchResponses();
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({
                    text: generic || 'Could not migrate all templates',
                });
            } finally {
                this.busy = false;
            }
        },
    },
};
</script>

<style>
</style>
